import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControl,
  FormLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { documentReviewAPI } from '../../services/api';

interface DocumentReviewSettings {
  enabled: boolean;
  reviewPeriodDays: number;
}

export const DocumentReviewSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<DocumentReviewSettings>({ enabled: false, reviewPeriodDays: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await documentReviewAPI.getSettings();
      setSettings(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await documentReviewAPI.updateSettings(settings);
      setSuccess('Settings updated successfully');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsChange = (field: keyof DocumentReviewSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'enabled' ? event.target.checked : parseInt(event.target.value) || 0;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Document Review Settings
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Enable Document Review
              </Typography>
            </FormLabel>
            <Box display="flex" alignItems="center" mt={1}>
              <Switch
                checked={settings.enabled}
                onChange={handleSettingsChange('enabled')}
                color="primary"
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {settings.enabled ? 'Enabled' : 'Disabled'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              When enabled, new users will be required to review and accept documents before accessing the system.
            </Typography>
          </FormControl>
        </Grid>

        {settings.enabled && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Review Period (Days)"
              type="number"
              value={settings.reviewPeriodDays}
              onChange={handleSettingsChange('reviewPeriodDays')}
              inputProps={{ min: 1, max: 365 }}
              helperText="Number of days users have to complete document review before losing access"
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving && <CircularProgress size={20} />}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button
              variant="outlined"
              onClick={loadSettings}
              disabled={saving}
            >
              Reset
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};